import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStory } from '../../contexts/StoryContext';
import { useAuth } from '../../contexts/AuthContext';
import CharacterManager from '../../containers/CharacterManager';
import StorySettingsForm from '../../components/StorySettingsForm';
import { Button } from '../../components/ui';
import WelcomeBackBanner from '../../components/WelcomeBackBanner/WelcomeBackBanner';
import './CharacterSelectView.css';

/**
 * CharacterSelectView component
 * Allows users to select characters and set story settings before starting a story
 */
const STORAGE_KEY = 'storySettingsFormData';

const CharacterSelectView = () => {
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [theme, setTheme] = useState('');
  const [desiredEnding, setDesiredEnding] = useState('');
  const [localError, setLocalError] = useState(null);
  const navigate = useNavigate();
  const { startStory, setError } = useStory();
  const { isAuthenticated, trackStoryCreation } = useAuth();
  
  // Load saved form data from localStorage on component mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const { theme: savedTheme, desiredEnding: savedEnding } = JSON.parse(savedData);
        if (savedTheme) setTheme(savedTheme);
        if (savedEnding) setDesiredEnding(savedEnding);
      }
    } catch (error) {
      console.error('Error loading saved story settings:', error);
    }
  }, []);

  // Handle character selection
  const handleCharacterSelect = useCallback((character, isSelected) => {
    setSelectedCharacters(prev => {
      if (isSelected) {
        // Add character if not already selected and under 3 characters
        return prev.some(c => c.id === character.id) 
          ? prev 
          : prev.length < 3 ? [...prev, character] : prev;
      } else {
        // Remove character
        return prev.filter(c => c.id !== character.id);
      }
    });
  }, []);

  // Save form data to localStorage whenever theme or desired ending changes
  useEffect(() => {
    const formData = {
      theme,
      desiredEnding
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [theme, desiredEnding]);
  
  // Custom handlers for theme and ending changes that save to localStorage
  const handleThemeChange = useCallback((newTheme) => {
    setTheme(newTheme);
  }, []);
  
  const handleEndingChange = useCallback((newEnding) => {
    setDesiredEnding(newEnding);
  }, []);
  
  // Handle starting a new story
  const handleStartStory = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    if (!theme.trim()) {
      setLocalError('Please enter a theme for your story');
      return;
    }
    
    if (!desiredEnding.trim()) {
      setLocalError('Please enter a desired ending for your story');
      return;
    }
    
    if (selectedCharacters.length === 0) {
      setLocalError('Please select at least one character');
      return;
    }
    
    try {
      const storyId = await startStory(theme, desiredEnding, selectedCharacters);
      if (storyId) {
        // Track story creation for anonymous users
        if (!isAuthenticated) {
          trackStoryCreation();
        }
        
        // Clear saved form data when successfully starting a story
        localStorage.removeItem(STORAGE_KEY);
        navigate(`/weaversworld/story/${storyId}`);
      }
    } catch (error) {
      console.error('Error starting story:', error);
      setLocalError('Failed to start story. Please try again.');
    }
  }, [theme, desiredEnding, selectedCharacters, startStory, navigate, setError, isAuthenticated, trackStoryCreation]);

  // Check if form is valid
  const isFormValid = selectedCharacters.length > 0 && theme.trim() && desiredEnding.trim();

  return (
    <div className="character-select-view">
      <header className="character-select-view__header">
        <div className="character-select-view__header-content">
          {/* Back to Home button removed */}
          <div className="character-select-view__title-container">
            <h1 className="character-select-view__title">Create a Story</h1>
          </div>
        </div>
      </header>

      <WelcomeBackBanner />

      {localError && (
        <div className="character-select-view__error" data-testid="error-message">{localError}</div>
      )}

      <div className="character-select-view__content">
        <div className="character-select-view__section">
          <h2 className="character-select-view__section-title">Characters</h2>
          <p className="character-select-view__section-description">
            Choose 1-3 for this story
          </p>
          
          <CharacterManager 
            selectedCharacters={selectedCharacters}
            onCharacterSelect={handleCharacterSelect}
          />
        </div>

        <div className="character-select-view__section">
          <h2 className="character-select-view__section-title">Story Settings</h2>
          
          <StorySettingsForm
            theme={theme}
            desiredEnding={desiredEnding}
            onThemeChange={handleThemeChange}
            onEndingChange={handleEndingChange}
          />
        </div>

        <div className="character-select-view__actions">
          <Button 
            onClick={handleStartStory} 
            disabled={!isFormValid}
            fullWidth
          >
            Start Story
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CharacterSelectView;